import { computed, ref } from 'vue'
import { Capacitor } from '@capacitor/core'
import { App as CapacitorApp } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import { Preferences } from '@capacitor/preferences'
import {
  createAuthorizationRequest,
  decodeJwt,
  exchangeCode,
  fetchUserInfo,
  OidcError,
  parseAuthorizationResponse,
  verifyIdToken,
} from '@/api/oidc'
import type { UserInfo } from '@/api/oidc'
import { oidcConfig, redirectUri } from '@/config/oidc'
import router from '@/router'

/**
 * Session utilisateur via le connecteur OIDC d'Olympe (pattern « composable
 * singleton » : état au niveau du module, comme `useCompetition`).
 *
 * Cycle d'une connexion :
 *
 * 1. `login()` fabrique la requête d'autorisation (`api/oidc.ts`), **persiste**
 *    `state` / `nonce` / `code_verifier` dans `@capacitor/preferences` — en
 *    navigateur la page est quittée puis rechargée, les `ref` ne survivraient
 *    pas — et ouvre la page de login d'Olympe : navigateur système en natif
 *    (`@capacitor/browser`, SFSafariViewController / Custom Tabs, jamais une
 *    webview interne, comme le veut la RFC 8252), redirection de la fenêtre
 *    sur le web ;
 * 2. Olympe renvoie sur l'URI de redirection avec `?code=…&state=…` : en natif,
 *    l'événement `appUrlOpen` de `@capacitor/app` (ou `getLaunchUrl()` si l'app
 *    a été lancée à froid par le lien) ; sur le web, l'URL de la page au
 *    démarrage ;
 * 3. `completeLogin` vérifie le `state`, échange le code, valide l'`id_token`,
 *    lit `userinfo` et persiste la session (jeton d'accès + profil).
 *
 * Olympe ne délivre pas de `refresh_token` : une session est gardée jusqu'à
 * `expiresAt`, puis on redemande une connexion. Il n'y a pas non plus de
 * `end_session_endpoint` dans la découverte : `logout()` est local (la session
 * d'Olympe dans le navigateur système, elle, reste ouverte).
 *
 * Persistance : `@capacitor/preferences` (jamais le storage web, cf. CLAUDE.md).
 * Le jeton d'accès y est en clair — acceptable pour un jeton court, à remplacer
 * par un plugin de stockage sécurisé (Keychain / Keystore) si sa durée de vie
 * devait s'allonger.
 */

export type AuthStatus =
  | 'idle'
  /** lecture de la session persistée au démarrage */
  | 'restoring'
  /** `oidcConfig.clientId` est vide : rien à tenter */
  | 'unconfigured'
  | 'anonymous'
  /** page de login ouverte, ou échange du code en cours */
  | 'authenticating'
  | 'authenticated'
  | 'error'

export interface AuthUser {
  /** identifiant stable du compte Olympe (`sub`) */
  id: string
  /** `preferred_username` complet, discriminant compris (`foo#123`) ; `null` si Olympe n'en rend pas */
  username: string | null
  /** le même sans discriminant (`foo`), pour les endroits étroits comme la tab bar */
  shortName: string | null
  email: string | null
  name: string | null
  picture: string | null
}

interface Session {
  accessToken: string
  /** timestamp en millisecondes */
  expiresAt: number
  user: AuthUser
}

interface PendingLogin {
  state: string
  nonce: string
  codeVerifier: string
  redirectUri: string
  /** timestamp en millisecondes : une tentative trop vieille est ignorée */
  startedAt: number
}

/** suffixe de version : à incrémenter quand la forme persistée change, l'ancienne session est alors ignorée */
const SESSION_KEY = 'oidc.session.v2'
const PENDING_KEY = 'oidc.pending'
/** au-delà, un retour de la page de login n'est plus attendu */
const PENDING_TTL_MS = 15 * 60 * 1000
/** sans `expires_in` ni `exp` exploitable */
const DEFAULT_TTL_MS = 60 * 60 * 1000

// --- État partagé ------------------------------------------------------------

const status = ref<AuthStatus>('idle')
const session = ref<Session | null>(null)
/** message d'erreur lisible, pour l'écran */
const error = ref<string | null>(null)
/** information non bloquante (session expirée, connexion annulée…) */
const notice = ref<string | null>(null)

let pendingLogin: PendingLogin | null = null
let initialized: Promise<void> | null = null

// --- Persistance -------------------------------------------------------------

async function readJson<T>(key: string): Promise<T | null> {
  try {
    const { value } = await Preferences.get({ key })
    return value ? (JSON.parse(value) as T) : null
  } catch {
    return null
  }
}

async function writeJson(key: string, value: unknown): Promise<void> {
  try {
    await Preferences.set({ key, value: JSON.stringify(value) })
  } catch (reason) {
    console.warn('[oidc] persistance impossible', key, reason)
  }
}

async function remove(key: string): Promise<void> {
  try {
    await Preferences.remove({ key })
  } catch {
    // rien à faire : la clé n'était peut-être pas là
  }
}

// --- Conversion --------------------------------------------------------------

/** `Pseudo#1234` → `Pseudo` : Olympe suffixe les pseudos d'un discriminant, comme pour les casters. */
function stripDiscriminant(value: string): string {
  const hash = value.indexOf('#')
  return (hash === -1 ? value : value.slice(0, hash)).trim()
}

function toUser(info: UserInfo, claims: Record<string, unknown>): AuthUser {
  const email = info.email ?? (typeof claims.email === 'string' ? claims.email : null)
  const preferred =
    info.preferred_username ??
    (typeof claims.preferred_username === 'string' ? claims.preferred_username : null)

  return {
    id: info.sub,
    username: preferred,
    shortName: preferred ? stripDiscriminant(preferred) || null : null,
    email,
    name: info.name,
    picture: info.picture,
  }
}

function describe(reason: unknown): string {
  if (reason instanceof OidcError) {
    switch (reason.code) {
      case 'invalid_client':
        return 'Olympe ne reconnaît pas cette application (client_id ou secret à vérifier).'
      case 'invalid_grant':
        return 'Le code de connexion a été refusé. Réessaie.'
      case 'state_mismatch':
        return 'La réponse de connexion ne correspond pas à la demande. Réessaie.'
      case 'denied':
        return 'Connexion refusée sur la page Olympe.'
      case 'network':
      case 'discovery':
        return 'Olympe est injoignable pour le moment.'
      case 'invalid_token':
        return 'Le jeton reçu est invalide.'
      case 'insecure_context':
        return 'Connexion impossible hors contexte sécurisé (https, capacitor ou localhost).'
      default:
        return reason.message
    }
  }
  return 'Connexion impossible.'
}

// --- Flux --------------------------------------------------------------------

/** Ferme le navigateur système ouvert par `login()` (iOS surtout ; sans effet ailleurs). */
async function closeBrowser(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  try {
    await Browser.close()
  } catch {
    // Android referme l'onglet personnalisé de lui-même
  }
}

/**
 * Traite une URL de retour. Rend `false` si l'URL n'est pas une réponse
 * d'autorisation (deep link quelconque, page ouverte normalement).
 */
async function completeLogin(url: string): Promise<boolean> {
  const response = parseAuthorizationResponse(url)
  if (!response) return false

  status.value = 'authenticating'
  error.value = null
  notice.value = null

  try {
    const pending = pendingLogin ?? (await readJson<PendingLogin>(PENDING_KEY))
    pendingLogin = null
    await remove(PENDING_KEY)

    if (!pending || Date.now() - pending.startedAt > PENDING_TTL_MS) {
      throw new OidcError('state_mismatch', 'aucune tentative de connexion en attente')
    }
    if (response.state !== pending.state) {
      throw new OidcError('state_mismatch', 'state inattendu')
    }
    if (response.error || !response.code) {
      const code = response.error === 'access_denied' ? 'denied' : 'unknown'
      throw new OidcError(code, response.errorDescription ?? response.error ?? 'réponse sans code')
    }

    const tokens = await exchangeCode({
      issuer: oidcConfig.issuer,
      clientId: oidcConfig.clientId,
      clientSecret: oidcConfig.clientSecret,
      code: response.code,
      codeVerifier: pending.codeVerifier,
      redirectUri: pending.redirectUri,
    })

    const claims = await verifyIdToken(tokens.id_token, {
      issuer: oidcConfig.issuer,
      clientId: oidcConfig.clientId,
      nonce: pending.nonce,
    })

    const info = await fetchUserInfo(oidcConfig.issuer, tokens.access_token)

    // durée de vie : `expires_in` fait foi, sinon l'`exp` du jeton d'accès
    // s'il est un JWT lisible, sinon une heure
    const accessClaims = decodeJwt<{ exp?: number }>(tokens.access_token)
    const expiresAt =
      tokens.expires_in !== null
        ? Date.now() + tokens.expires_in * 1000
        : typeof accessClaims?.exp === 'number'
          ? accessClaims.exp * 1000
          : Date.now() + DEFAULT_TTL_MS

    session.value = { accessToken: tokens.access_token, expiresAt, user: toUser(info, claims) }
    await writeJson(SESSION_KEY, session.value)
    status.value = 'authenticated'
  } catch (reason) {
    console.warn('[oidc] connexion échouée', reason)
    session.value = null
    if (reason instanceof OidcError && reason.code === 'denied') {
      notice.value = describe(reason)
      status.value = 'anonymous'
    } else {
      error.value = describe(reason)
      status.value = 'error'
    }
  } finally {
    await closeBrowser()
  }

  // retour sur l'onglet Profil, et on efface le code de l'URL en navigateur
  // (il a été consommé, un rechargement ne doit pas le rejouer)
  await router.replace({ name: 'profile' })
  if (!Capacitor.isNativePlatform() && window.location.search) {
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.hash}`,
    )
  }

  return true
}

async function restoreSession(): Promise<void> {
  const stored = await readJson<Session>(SESSION_KEY)
  if (!stored || typeof stored.accessToken !== 'string' || typeof stored.expiresAt !== 'number') {
    status.value = 'anonymous'
    return
  }
  if (stored.expiresAt <= Date.now()) {
    await remove(SESSION_KEY)
    notice.value = 'Ta session a expiré, reconnecte-toi.'
    status.value = 'anonymous'
    return
  }
  // la session a pu être écrite par une version antérieure : la forme courte
  // est recalculée depuis le username plutôt que reprise telle quelle
  const username = typeof stored.user?.username === 'string' ? stored.user.username : null
  session.value = {
    ...stored,
    user: { ...stored.user, username, shortName: username ? stripDiscriminant(username) || null : null },
  }
  status.value = 'authenticated'
}

/**
 * Démarrage : branche l'écoute des liens de retour, traite un retour déjà
 * présent (lancement à froid par le lien, ou page web rechargée avec le code),
 * sinon restaure la session persistée. Idempotent.
 */
function initAuth(): Promise<void> {
  if (initialized) return initialized

  initialized = (async () => {
    status.value = 'restoring'

    if (Capacitor.isNativePlatform()) {
      await CapacitorApp.addListener('appUrlOpen', ({ url }) => {
        void completeLogin(url)
      })
    }

    if (!oidcConfig.clientId) {
      status.value = 'unconfigured'
      return
    }

    // un retour de la page de login peut être l'URL qui a lancé l'app
    let launchUrl: string | null = null
    if (Capacitor.isNativePlatform()) {
      try {
        launchUrl = (await CapacitorApp.getLaunchUrl())?.url ?? null
      } catch {
        launchUrl = null
      }
    } else {
      launchUrl = window.location.href
    }

    if (launchUrl && (await completeLogin(launchUrl))) return
    await restoreSession()
  })()

  return initialized
}

/** Ouvre la page de login d'Olympe. Sans `clientId`, ne fait rien. */
async function login(): Promise<void> {
  if (!oidcConfig.clientId) {
    status.value = 'unconfigured'
    return
  }
  if (status.value === 'authenticating') return

  status.value = 'authenticating'
  error.value = null
  notice.value = null

  try {
    const request = await createAuthorizationRequest({
      issuer: oidcConfig.issuer,
      clientId: oidcConfig.clientId,
      scopes: oidcConfig.scopes,
      redirectUri: redirectUri(),
    })

    pendingLogin = {
      state: request.state,
      nonce: request.nonce,
      codeVerifier: request.codeVerifier,
      redirectUri: request.redirectUri,
      startedAt: Date.now(),
    }
    await writeJson(PENDING_KEY, pendingLogin)

    if (Capacitor.isNativePlatform()) {
      await Browser.open({ url: request.url, presentationStyle: 'popover' })
    } else {
      window.location.assign(request.url)
    }
  } catch (reason) {
    console.warn('[oidc] ouverture de la connexion impossible', reason)
    error.value = describe(reason)
    status.value = 'error'
  }
}

/** Sortie locale : la session persistée est effacée, le compte Olympe reste connecté dans le navigateur. */
async function logout(): Promise<void> {
  session.value = null
  error.value = null
  notice.value = null
  await remove(SESSION_KEY)
  status.value = 'anonymous'
}

/**
 * Abandonne une tentative en cours (l'utilisateur est revenu sans se
 * connecter, en fermant le navigateur système par exemple).
 */
async function cancelLogin(): Promise<void> {
  if (status.value !== 'authenticating') return
  pendingLogin = null
  await remove(PENDING_KEY)
  status.value = session.value ? 'authenticated' : 'anonymous'
}

export function useAuth() {
  const user = computed<AuthUser | null>(() => session.value?.user ?? null)
  const expiresAt = computed<Date | null>(() =>
    session.value ? new Date(session.value.expiresAt) : null,
  )
  /** le jeton à envoyer en `Authorization: Bearer` aux routes Olympe qui exigent une session */
  const accessToken = computed<string | null>(() => session.value?.accessToken ?? null)

  return {
    status,
    user,
    expiresAt,
    accessToken,
    error,
    notice,
    initAuth,
    login,
    logout,
    cancelLogin,
  }
}
